// ═══════════════════════════════════════════════════════════════
// MÓDULO DE PROCESAMIENTO DE NÓMINA
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Calculator,
  Calendar,
  AlertCircle,
  CheckCircle,
  DollarSign,
  FileText,
  Lock,
  Play,
  Eye,
  Download,
  XCircle,
  Clock,
  SlidersHorizontal,
  X,
  RefreshCw,
  Plus,
} from 'lucide-react';
import Pagination from '../../common/Pagination';
import { useCompany } from '../../../contexts/CompanyContext';
import useEmployees from '../../../hooks/useEmployees';
import { usePayroll } from '../../../hooks/usePayroll';
import PayrollCreationModal from './PayrollCreationModal';
import PayrollSimulationView from './PayrollSimulationView';
import PayrollProcessingView from './PayrollProcessingView';
import PayrollDetailsView from './PayrollDetailsView';

// ── Helpers ──────────────────────────────────────────────────────
const formatCurrency = (value) =>
  parseFloat(value || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PER_PAGE = 12;

const ESTADO_CONFIG = {
  draft:      { label: 'Borrador',   icon: FileText,     cls: 'bg-gray-100 text-gray-700'   },
  processing: { label: 'Procesando', icon: Clock,        cls: 'bg-blue-100 text-blue-700'   },
  processed:  { label: 'Procesada',  icon: CheckCircle,  cls: 'bg-emerald-100 text-emerald-700' },
  closed:     { label: 'Cerrada',    icon: Lock,         cls: 'bg-green-100 text-green-700' },
  cancelled:  { label: 'Anulada',    icon: XCircle,      cls: 'bg-red-100 text-red-700'     },
};

const TIPO_CONFIG = {
  quincenal:    { label: 'Quincenal',    cls: 'bg-blue-50 text-blue-700'    },
  mensual:      { label: 'Mensual',      cls: 'bg-violet-50 text-violet-700' },
  especial:     { label: 'Especial',     cls: 'bg-amber-50 text-amber-700'   },
  bonificacion: { label: 'Bonificación', cls: 'bg-green-50 text-green-700'   },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function TipoBadge({ tipo }) {
  const cfg = TIPO_CONFIG[tipo] || TIPO_CONFIG.quincenal;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════
export default function PayrollProcessingModule() {
  const { currentCompany } = useCompany();
  const { employees } = useEmployees();
  const { loading, error, getPayrolls, createPayroll, clearError } = usePayroll();

  const [payrolls, setPayrolls]               = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [view, setView]                       = useState('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters]         = useState(false);
  const [isRefreshing, setIsRefreshing]       = useState(false);
  const [currentPage, setCurrentPage]         = useState(1);

  const [filters, setFilters] = useState({ periodo: '', estado: 'all', tipo: 'all' });

  useEffect(() => {
    if (currentCompany?.id) loadPayrolls();
  }, [currentCompany?.id, filters]); // eslint-disable-line

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filters]);

  const loadPayrolls = async () => {
    try {
      const queryFilters = {};
      if (filters.estado !== 'all') queryFilters.estado = filters.estado;
      if (filters.tipo   !== 'all') queryFilters.tipo   = filters.tipo;
      if (filters.periodo)          queryFilters.periodo = filters.periodo;

      const data = await getPayrolls(currentCompany.id, queryFilters);

      let list = Array.isArray(data) ? data
               : Array.isArray(data?.payrolls) ? data.payrolls
               : Array.isArray(data?.data)     ? data.data
               : [];

      // Las nóminas de bonificación se gestionan en su propio módulo.
      // Solo se muestran aquí si el usuario las filtra explícitamente.
      if (filters.tipo === 'all') {
        list = list.filter(p => p.tipo !== 'bonificacion');
      }

      setPayrolls(list);
    } catch {
      setPayrolls([]);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadPayrolls();
    setIsRefreshing(false);
  };

  const handleCreatePayroll = async (data) => {
    try {
      clearError();

      if (!data.employeeIds || !Array.isArray(data.employeeIds) || data.employeeIds.length === 0)
        throw new Error('Debes seleccionar al menos un empleado');

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const invalidIds = data.employeeIds.filter(id => !uuidRegex.test(id));
      if (invalidIds.length > 0) throw new Error('Algunos IDs de empleados no son válidos');

      const newPayroll = await createPayroll({
        companyId:   currentCompany.id,
        periodo:     data.periodo,
        quincena:    data.quincena,
        tipo:        data.tipo,
        fechaInicio: data.fechaInicio,
        fechaFin:    data.fechaFin,
        fechaPago:   data.fechaPago,
        descripcion: data.descripcion || `Nómina ${data.tipo} ${data.periodo}${data.quincena ? ` - ${data.quincena}` : ''}`,
        employeeIds: data.employeeIds,
        createdBy:   localStorage.getItem('user_id') || null
      });

      setShowCreateModal(false);
      setSelectedPayroll(newPayroll);
      setView('simulate');
      await loadPayrolls();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error desconocido';
      alert(`Error al crear nómina:\n\n${msg}`);
    }
  };

  const handleSelectPayroll = (payroll) => {
    setSelectedPayroll(payroll);
    if (payroll.estado === 'draft')           setView('simulate');
    else if (payroll.estado === 'processing') setView('process');
    else                                      setView('details');
  };

  // ── Stats ───────────────────────────────────────────────────────
  const safePayrolls = Array.isArray(payrolls) ? payrolls : [];
  const stats = {
    total:       safePayrolls.length,
    enProceso:   safePayrolls.filter(p => p.estado === 'draft' || p.estado === 'processing').length,
    cerradas:    safePayrolls.filter(p => p.estado === 'closed' || p.estado === 'processed').length,
    totalPagado: safePayrolls
      .filter(p => p.estado === 'closed' || p.estado === 'processed')
      .reduce((s, p) => s + parseFloat(p.total_neto || 0), 0),
  };

  // ── Pagination ──────────────────────────────────────────────────
  const totalPages  = Math.max(1, Math.ceil(safePayrolls.length / PER_PAGE));
  const safePage    = Math.min(currentPage, totalPages);
  const pageStart   = (safePage - 1) * PER_PAGE;
  const paginated   = safePayrolls.slice(pageStart, pageStart + PER_PAGE);

  const hasFilters = filters.estado !== 'all' || filters.tipo !== 'all' || filters.periodo !== '';

  // ── Sub-vistas ──────────────────────────────────────────────────
  const backToList = async () => {
    setView('list');
    setSelectedPayroll(null);
    await loadPayrolls();
  };

  if (view === 'simulate' && selectedPayroll) {
    return <PayrollSimulationView payroll={selectedPayroll} onBack={backToList} onProcess={() => setView('process')} />;
  }
  if (view === 'process' && selectedPayroll) {
    return <PayrollProcessingView payroll={selectedPayroll} onBack={backToList} onComplete={async () => { setView('details'); await loadPayrolls(); }} />;
  }
  if (view === 'details' && selectedPayroll) {
    return <PayrollDetailsView payroll={selectedPayroll} onBack={backToList} />;
  }

  // ── Vista lista principal ───────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
            <span className="font-semibold text-gray-700">{stats.total}</span> nóminas
          </span>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing || loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
              showFilters || hasFilters
                ? 'border-blue-400 text-blue-600 bg-blue-50'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal size={15} />
            Filtros
            {hasFilters && <span className="w-2 h-2 rounded-full bg-blue-500" />}
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            Nueva Nómina
          </button>
        </div>
      </div>

      {/* ── Error banner ─────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="flex-1 text-red-700">{error}</span>
          <button onClick={clearError} className="text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Stats Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Total Nóminas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">En Proceso</p>
              <p className="text-2xl font-bold text-amber-600">{stats.enProceso}</p>
            </div>
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Cerradas</p>
              <p className="text-2xl font-bold text-green-600">{stats.cerradas}</p>
            </div>
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Total Pagado</p>
              <p className="text-base font-bold text-violet-600 leading-tight">
                RD$ {formatCurrency(stats.totalPagado)}
              </p>
            </div>
            <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-violet-600" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Filtros colapsables ───────────────────────────────────── */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="month"
              value={filters.periodo}
              onChange={(e) => setFilters(f => ({ ...f, periodo: e.target.value }))}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
            />
          </div>

          <select
            value={filters.estado}
            onChange={(e) => setFilters(f => ({ ...f, estado: e.target.value }))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="processing">Procesando</option>
            <option value="processed">Procesada</option>
            <option value="closed">Cerrada</option>
            <option value="cancelled">Anulada</option>
          </select>

          <select
            value={filters.tipo}
            onChange={(e) => setFilters(f => ({ ...f, tipo: e.target.value }))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">Todos los tipos</option>
            <option value="quincenal">Quincenal</option>
            <option value="mensual">Mensual</option>
            <option value="especial">Especial</option>
            <option value="bonificacion">Bonificación</option>
          </select>

          {hasFilters && (
            <button
              onClick={() => setFilters({ periodo: '', estado: 'all', tipo: 'all' })}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      )}

      {/* ── Tabla de nóminas ─────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Cargando nóminas...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Período</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tipo</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Quincena</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Empleados</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Bruto</th>
                  <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Neto</th>
                  <th className="px-5 py-3 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Estado</th>
                  <th className="px-5 py-3 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {safePayrolls.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-16 text-center">
                      <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500 mb-1">No hay nóminas registradas</p>
                      <p className="text-xs text-gray-400">Crea una nueva nómina para comenzar</p>
                    </td>
                  </tr>
                ) : (
                  paginated.map((payroll) => (
                    <tr key={payroll.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-semibold text-gray-900">{payroll.periodo}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {payroll.fecha_inicio ? new Date(payroll.fecha_inicio.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO') : '—'}{' – '}
                          {payroll.fecha_fin    ? new Date(payroll.fecha_fin.split('T')[0]    + 'T12:00:00').toLocaleDateString('es-DO') : '—'}
                        </p>
                      </td>
                      <td className="px-5 py-3.5">
                        <TipoBadge tipo={payroll.tipo} />
                      </td>
                      <td className="px-5 py-3.5">
                        {payroll.quincena && (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            payroll.quincena === 'Q1' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                          }`}>
                            {payroll.quincena}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm font-semibold text-gray-900">{payroll.total_empleados || 0}</span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm text-gray-700">RD$ {formatCurrency(payroll.total_bruto)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-sm font-semibold text-blue-700">RD$ {formatCurrency(payroll.total_neto)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <EstadoBadge estado={payroll.estado} />
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleSelectPayroll(payroll)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Ver
                          </button>
                          {(payroll.estado === 'closed' || payroll.estado === 'processed') && (
                            <button className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors">
                              <Download className="w-3.5 h-3.5" />
                              Exportar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ───────────────────────────────────────────── */}
        {!loading && (
          <Pagination
            page={safePage}
            totalPages={totalPages}
            total={safePayrolls.length}
            pageSize={PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}

        {/* ── Footer totales (sin paginación) ─────────────────────── */}
        {!loading && safePayrolls.length > 0 && safePayrolls.length <= PER_PAGE && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">{safePayrolls.length}</span>{' '}
              nómina{safePayrolls.length !== 1 ? 's' : ''}
              {stats.cerradas > 0 && (
                <> · Total pagado:{' '}
                  <span className="font-semibold text-gray-700">RD$ {formatCurrency(stats.totalPagado)}</span>
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {/* ── Modal de creación ─────────────────────────────────────── */}
      {showCreateModal && (
        <PayrollCreationModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePayroll}
          employees={employees}
        />
      )}
    </div>
  );
}
